import { useEffect, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next"; // if you use react-i18next
import { onApiCallVisitSend } from "../../Core/Apicall";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ReCAPTCHA from "react-google-recaptcha";
import { sitekey } from "../../Core/ApiEndPoint";

const NAVY = "#1F5C8A";

function useKeydown(key, handler) {
    // stable handler via useCallback recommended by consumer
    useEffect(() => {
        const fn = (e) => {
            if (e.key === key) handler(e);
        };
        window.addEventListener("keydown", fn);
        return () => window.removeEventListener("keydown", fn);
    }, [key, handler]);
}

export default function VisitModal({ open, onClose, initialUnit = "", profileData }) {
    const { t, i18n } = useTranslation?.() || { t: (s) => s };
    const { id } = useParams();
    const [fullName, setFullName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [moveInDate, setMoveInDate] = useState("");
    const [unit, setUnit] = useState(initialUnit);
    const [submitting, setSubmitting] = useState(false);
    const [msg, setMsg] = useState(null);
    const [captchaToken, setCaptchaToken] = useState(null);


    const modalRef = useRef(null);
    const firstInputRef = useRef(null);

    const lang = i18n.language;

    // keep unit in sync if initialUnit prop changes or modal reopens
    useEffect(() => {
        if (open) setUnit(initialUnit || "");
    }, [initialUnit, open]);

    // Close on Escape
    const handleEscape = useCallback(() => {
        if (open) onClose();
    }, [open, onClose]);

    useKeydown("Escape", handleEscape);

    // Focus first input when opened (more reliable than setTimeout)
    useEffect(() => {
        if (!open) return;
        // prevent background scroll
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        // focus in next frame
        const id = requestAnimationFrame(() => firstInputRef.current?.focus());

        return () => {
            cancelAnimationFrame(id);
            document.body.style.overflow = prev;
        };
    }, [open]);

    // Focus trap: keep tab focus inside modal
    useEffect(() => {
        if (!open) return;
        const node = modalRef.current;
        if (!node) return;

        function onKeyDown(e) {
            if (e.key !== "Tab") return;
            const focusable = node.querySelectorAll(
                'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
            );
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }

        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [open]);

    function validate() {
        if (!fullName.trim()) return t("Please enter your full name.") || "Please enter your full name.";
        if (!phone.trim()) return t("Please enter a phone number.") || "Please enter a phone number.";
        if (!email.trim()) return t("Please enter an email address.") || "Please enter an email address.";
        return null;
    }

    async function handleSubmit(e) {
        e.preventDefault();

        const data = {
            property_id: id,
            full_name: fullName,
            phone,
            email,
            move_in_date: moveInDate,
            unit,
            captchaToken
        };
        const params = `/?lang=${lang}`
        try {
            const res = await onApiCallVisitSend(data, params);
            setFullName("");
            setPhone("");
            setEmail("");
            setMoveInDate("");
            setUnit(initialUnit || "");
            onClose();
            toast.success(res?.message || "Send successful");
        } catch (err) {
            console.log(err);
            toast.error(err?.response?.data?.message || "Something went wrong!");

        } finally {
            setSubmitting(false);
        }
    }

    if (!open) return null;

    return ReactDOM.createPortal(
        <div
            className="visit-modal-outer"
            aria-modal="true"
            role="dialog"
            aria-labelledby="visit-modal-title"
            onMouseDown={(e) => {
                // close when clicking overlay outside modal (only if click is on overlay itself)
                if (e.target === e.currentTarget) onClose();
            }}
            // help screen readers by preventing accidental focus on overlay
            tabIndex={-1}
        >
            <style>{`
        .visit-modal-outer {
          position: fixed;
          inset: 0;
          display: grid;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,0.45);
          z-index: 10000;
          padding: 24px;
          overflow: auto;
        }
        .visit-modal {
          max-width: 680px;
          background: #fff;
          border-radius: 6px;
          box-shadow: 0 12px 40px rgba(10,20,40,0.25);
          padding: 36px 48px;
          position: relative;
          font-family: Inter, system-ui, Arial, sans-serif;
        }
        .visit-modal h2 {
          margin: 0 0 10px;
          text-align: center;
          font-size: 28px;
          font-weight: 700;
        }
        .visit-modal .subtitle {
          text-align: center;
          margin-bottom: 20px;
          color: #333;
          font-size: 14px;
        }
        .visit-form {
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
          margin-top: 6px;
        }
        .visit-form input, .visit-form select {
          width: 100%;
          padding: 12px 14px;
          border-radius: 4px;
          border: 1px solid #d7dbe0;
          box-sizing: border-box;
          font-size: 14px;
        }
        .visit-form .row-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .visit-form .unit {
          background: #f5f7fb;
        }
        .visit-actions {
          display:flex;
          justify-content:center;
          margin-top:18px;
        }
        .visit-actions button {
          padding: 12px 26px;
          background: ${NAVY};
          color: #fff;
          border: none;
          border-radius: 6px;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 6px 14px rgba(31,92,138,0.18);
        }
        .visit-close {
          position: absolute;
          right: 14px;
          top: 12px;
          border: 0;
          background: transparent;
          font-size: 20px;
          cursor: pointer;
          color: #333;
        }
        .visit-msg { margin-top:10px; text-align:center; font-size:14px; }
        .visit-msg.error { color: #b00020; }
        .visit-msg.success { color: green; }
        .visit-msg.info { color: #333; opacity: 0.9; }
      `}</style>

            <div className="visit-modal" ref={modalRef}>
                <button
                    className="visit-close"
                    aria-label={t("Close") || "Close"}
                    onClick={onClose}
                >
                    ×
                </button>

                <div style={{ borderBottom: `1px solid #e6eef9`, paddingBottom: 10, marginBottom: 18 }}>
                    <h2 id="visit-modal-title">{t("Fill Out The Form Below To\nSchedule A Visit") || "Fill Out The Form Below To\nSchedule A Visit"}</h2>
                </div>

                <form className="visit-form" onSubmit={handleSubmit}>
                    <input
                        ref={firstInputRef}
                        type="text"
                        placeholder={t("Full Name") || "Full Name"}
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        aria-label={t("Full name") || "Full name"}
                        required
                    />

                    <input
                        type="tel"
                        placeholder={t("Phone number") || "Phone number"}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        aria-label={t("Phone number") || "Phone number"}
                        required
                    />

                    <input
                        type="email"
                        placeholder={t("Email") || "Email"}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        aria-label={t("Email") || "Email"}
                        required
                    />

                    <input
                        type="date"
                        placeholder={t("Desired Move-in date") || "Desired Move-in date"}
                        value={moveInDate}
                        onChange={(e) => setMoveInDate(e.target.value)}
                        aria-label={t("Move-in date") || "Move-in date"}
                    />

                    <select value={unit} onChange={(e) => setUnit(e.target.value)} aria-label={t("Unit") || "Unit"}>
                        <option value={initialUnit} >{initialUnit || (t("Select unit / property") || "Select unit / property")}</option>
                        {/* <option value="1 Bed 1 Bath">{t("1 Bed 1 Bath – Boulevard des Laurentides") || "1 Bed 1 Bath – Boulevard des Laurentides"}</option>
                        <option value="2 Bed 1 Bath">{t("2 Bed 1 Bath – Example Apt") || "2 Bed 1 Bath – Example Apt"}</option>
                        <option value="Studio">{t("Studio – Example Unit") || "Studio – Example Unit"}</option> */}
                    </select>
                    <ReCAPTCHA
                        sitekey={sitekey}
                        required
                        onChange={(token) => setCaptchaToken(token)}
                    />

                    <div className="visit-actions">
                        <button type="submit" disabled={submitting}>
                            {submitting ? (t("Submitting…") || "Submitting…") : (t("Schedule Now") || "Schedule Now")}
                        </button>
                    </div>

                    {msg && (
                        <div className={`visit-msg ${msg.type === "error" ? "error" : msg.type === "success" ? "success" : "info"}`}>
                            {msg.text}
                        </div>
                    )}
                </form>
            </div>
        </div>,
        document.body
    );
}
